import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getOfficeLedger, getOfficeLedgerSummary, createOfficeLedger,
  updateOfficeLedger, deleteOfficeLedger, getOfficeLedgerRecipients,
} from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Plus, PencilSimple, Trash, FileXls, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  salary: 'Salary',
  incentive: 'Incentive',
  office_expense: 'Office Expense',
  owner_expense: 'Owner Expense',
};

const CATEGORY_COLORS = {
  salary: 'text-blue-700 bg-blue-50',
  incentive: 'text-emerald-700 bg-emerald-50',
  office_expense: 'text-amber-700 bg-amber-50',
  owner_expense: 'text-purple-700 bg-purple-50',
};

const ROLE_LABELS = {
  staff: 'Staff',
  sub_staff: 'Sub-Staff',
  accountant: 'CA',
  assistant: 'Assistant',
  sales_person: 'Sales Person',
};

const fmtINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function OfficeLedgerTab() {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = role === 'owner' || role === 'admin';

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ by_category: {}, grand_total: 0 });
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const today = new Date();
  const initialForm = {
    category: 'salary',
    amount: '',
    staff_id: '',
    utr_number: '',
    payment_date: today.toISOString().slice(0, 10),
    payment_time: today.toTimeString().slice(0, 5),
    notes: '',
  };
  const [form, setForm] = useState(initialForm);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterStaff) params.staff_id = filterStaff;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [{ data }, { data: summ }] = await Promise.all([
        getOfficeLedger(params),
        getOfficeLedgerSummary({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
      ]);
      setRows(data || []);
      setSummary(summ || { by_category: {}, grand_total: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Office Ledger');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStaff, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (canEdit) {
      getOfficeLedgerRecipients()
        .then(({ data }) => setRecipients(Array.isArray(data) ? data : []))
        .catch(() => setRecipients([]));
    }
  }, [canEdit]);

  const openCreate = () => {
    setEditing(null);
    const now = new Date();
    setForm({
      ...initialForm,
      payment_date: now.toISOString().slice(0, 10),
      payment_time: now.toTimeString().slice(0, 5),
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      category: row.category,
      amount: String(row.amount || ''),
      staff_id: row.staff_id || '',
      utr_number: row.utr_number || '',
      payment_date: row.payment_date || today.toISOString().slice(0, 10),
      payment_time: row.payment_time || today.toTimeString().slice(0, 5),
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Amount > 0 required'); return;
    }
    const needRecipient = form.category !== 'owner_expense';
    if (needRecipient && !form.staff_id) {
      toast.error('Recipient select karein'); return;
    }
    try {
      const payload = {
        category: form.category,
        amount: parseFloat(form.amount),
        staff_id: form.staff_id || undefined,
        utr_number: form.utr_number,
        payment_date: form.payment_date,
        payment_time: form.payment_time,
        notes: form.notes,
      };
      if (editing) {
        await updateOfficeLedger(editing.id, {
          amount: payload.amount,
          utr_number: payload.utr_number,
          payment_date: payload.payment_date,
          payment_time: payload.payment_time,
          notes: payload.notes,
        });
        toast.success('Entry updated');
      } else {
        await createOfficeLedger(payload);
        toast.success('Entry added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${CATEGORY_LABELS[row.category]} entry of ${fmtINR(row.amount)}?`)) return;
    try {
      await deleteOfficeLedger(row.id);
      toast.success('Entry deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadExport = async (kind) => {
    try {
      const { default: api } = await import('../lib/api');
      const res = await api.get(`/office-ledger/export/${kind}`, {
        params: {
          category: filterCategory || undefined,
          staff_id: filterStaff || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: kind === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      a.download = match ? match[1] : `office_ledger.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="office-ledger-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {['salary', 'incentive', 'office_expense', 'owner_expense'].map(cat => {
          const s = summary.by_category?.[cat] || { total: 0, count: 0 };
          return (
            <div key={cat} className={`p-4 border ${CATEGORY_COLORS[cat]} rounded-md`} data-testid={`summary-${cat}`}>
              <div className="text-xs uppercase tracking-wide font-medium">{CATEGORY_LABELS[cat]}</div>
              <div className="text-lg font-bold mt-1">{fmtINR(s.total)}</div>
              <div className="text-[11px] opacity-80">{s.count} entries</div>
            </div>
          );
        })}
        <div className="p-4 border border-gray-300 bg-gray-900 text-white rounded-md" data-testid="summary-grand-total">
          <div className="text-xs uppercase tracking-wide font-medium opacity-80">Grand Total</div>
          <div className="text-lg font-bold mt-1">{fmtINR(summary.grand_total)}</div>
          <div className="text-[11px] opacity-70">All categories</div>
        </div>
      </div>

      {/* Filters & Add */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Category</Label>
          <select className="form-input h-9 text-sm" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} data-testid="filter-category">
            <option value="">All</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Recipient</Label>
          <select className="form-input h-9 text-sm" value={filterStaff} onChange={e => setFilterStaff(e.target.value)} data-testid="filter-staff">
            <option value="">All</option>
            {canEdit
              ? recipients.map(s => <option key={s.id} value={s.id}>{s.name} ({ROLE_LABELS[s.role] || s.role})</option>)
              : Array.from(new Map(rows.filter(r => r.staff_id).map(r => [r.staff_id, r.staff_name])).entries())
                  .map(([id, nm]) => <option key={id} value={id}>{nm}</option>)
            }
          </select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-36" data-testid="filter-date-to" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            className="h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => downloadExport('excel')}
            data-testid="office-ledger-export-excel"
          >
            <FileXls size={14} className="mr-1" /> Excel
          </Button>
          <Button
            variant="outline"
            className="h-9 text-xs border-red-300 text-red-700 hover:bg-red-50"
            onClick={() => downloadExport('pdf')}
            data-testid="office-ledger-export-pdf"
          >
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#002FA7] hover:bg-[#001f7d] text-white" onClick={openCreate} data-testid="office-ledger-add">
                  <Plus size={16} className="mr-1" /> Add Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editing ? 'Edit Office Ledger Entry' : 'Add Office Ledger Entry'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <Label className="text-xs">Category *</Label>
                    <select
                      className="form-input"
                      value={form.category}
                      onChange={e => setForm(p => ({ ...p, category: e.target.value, staff_id: e.target.value === 'owner_expense' ? '' : p.staff_id }))}
                      disabled={!!editing}
                      data-testid="form-category"
                    >
                      <option value="salary">Salary</option>
                      <option value="incentive">Incentive</option>
                      <option value="office_expense">Office Expense</option>
                      <option value="owner_expense">Owner Expense</option>
                    </select>
                  </div>
                  {form.category !== 'owner_expense' && (
                    <div>
                      <Label className="text-xs">Recipient (Staff / CA / Assistant / Sales Person) *</Label>
                      <select
                        className="form-input"
                        value={form.staff_id}
                        onChange={e => setForm(p => ({ ...p, staff_id: e.target.value }))}
                        disabled={!!editing}
                        data-testid="form-staff"
                      >
                        <option value="">-- Select --</option>
                        {recipients.map(s => (
                          <option key={s.id} value={s.id}>{s.name} — {ROLE_LABELS[s.role] || s.role}</option>
                        ))}
                      </select>
                      {recipients.length === 0 && <p className="text-[11px] text-amber-600 mt-1">No recipients loaded. Reload karein.</p>}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Amount (₹) *</Label>
                      <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} required data-testid="form-amount" />
                    </div>
                    <div>
                      <Label className="text-xs">UTR / Ref No.</Label>
                      <Input value={form.utr_number} onChange={e => setForm(p => ({ ...p, utr_number: e.target.value }))} placeholder="Optional" data-testid="form-utr" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Date *</Label>
                      <Input type="date" value={form.payment_date} onChange={e => setForm(p => ({ ...p, payment_date: e.target.value }))} required data-testid="form-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Time</Label>
                      <Input type="time" value={form.payment_time} onChange={e => setForm(p => ({ ...p, payment_time: e.target.value }))} data-testid="form-time" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Purpose / details" data-testid="form-notes" />
                  </div>
                  <Button type="submit" className="w-full bg-[#002FA7] hover:bg-[#001f7d] text-white" data-testid="form-submit">
                    {editing ? 'Update Entry' : 'Save Entry'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Date / Time</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>UTR / Ref</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Entered By</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canEdit ? 8 : 7} className="text-center text-gray-400 py-6">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={canEdit ? 8 : 7} className="text-center text-gray-400 py-6">No entries yet</TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.id} data-testid={`office-ledger-row-${r.id}`}>
                <TableCell className="text-sm">
                  <div>{r.payment_date}</div>
                  <div className="text-[11px] text-gray-500">{r.payment_time}</div>
                </TableCell>
                <TableCell>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${CATEGORY_COLORS[r.category] || 'bg-gray-100'}`}>
                    {CATEGORY_LABELS[r.category] || r.category}
                  </span>
                </TableCell>
                <TableCell className="font-medium">
                  {r.category === 'owner_expense' ? (
                    <span className="text-purple-700">Owner</span>
                  ) : (
                    r.staff_name || '—'
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold">{fmtINR(r.amount)}</TableCell>
                <TableCell className="font-mono text-xs">{r.utr_number || '—'}</TableCell>
                <TableCell className="text-sm max-w-[220px] truncate" title={r.notes}>{r.notes || '—'}</TableCell>
                <TableCell className="text-xs text-gray-600">{r.created_by_name}<div className="text-[10px] opacity-70">{r.created_by_role}</div></TableCell>
                {canEdit && (
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(r)} data-testid={`delete-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
